import React from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Paper from '@mui/material/Paper';
import { ZSpinner } from '@marageti/z4-lib';
import { EntityType, AnyEntityVersion, getEntityVersionStatus } from '../../types';

interface VersionDetailViewProps {
  version: AnyEntityVersion | null;
  entityType: EntityType;
  entityId: string;
  isLoading: boolean;
  error: Error | null;
}

const DetailItem = ({ label, children }: { label: string; children: React.ReactNode }) => (
  <Box sx={{ mb: 2 }}>
    <Typography variant="subtitle2" color="text.secondary">{label}</Typography>
    {children}
  </Box>
);

interface VersionContentProps {
  version: AnyEntityVersion;
  entityType: EntityType;
  entityId: string;
}

const VersionDetailContent = ({ version, entityType, entityId }: VersionContentProps) => {
  const status = getEntityVersionStatus(version);

  return (
    <>
      <DetailItem label="Entity Type">
        <Typography variant="body1">{entityType}</Typography>
      </DetailItem>
      <DetailItem label="Entity ID">
        <Typography variant="body1">{entityId}</Typography>
      </DetailItem>
      <DetailItem label="Update ID">
        <Typography variant="body1">{version.updateId}</Typography>
      </DetailItem>
      <DetailItem label="Updated At">
        <Typography variant="body1">
          {new Date(version.updatedAt).toLocaleString()}
        </Typography>
      </DetailItem>
      {status && (
        <DetailItem label="Status">
          <Typography variant="body1">{status}</Typography>
        </DetailItem>
      )}
      <DetailItem label="Full Entity Data">
        <Paper
          sx={{
            p: 2,
            mt: 1,
            bgcolor: 'grey.100',
            overflow: 'auto',
            maxHeight: 500,
          }}
        >
          <pre style={{ margin: 0 }}>
            {JSON.stringify(version, null, 2)}
          </pre>
        </Paper>
      </DetailItem>
    </>
  );
};

const VersionDetailView: React.FC<VersionDetailViewProps> = ({
  version,
  entityType,
  entityId,
  isLoading,
  error,
}) => {
  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" py={4}>
        <ZSpinner />
      </Box>
    );
  }

  if (error) {
    return (
      <Typography color="error">
        Error:
        {' '}
        {error.message}
      </Typography>
    );
  }

  if (!version) {
    return (
      <Typography color="text.secondary">
        Version not found
      </Typography>
    );
  }

  return (
    <Box>
      <Typography variant="h5" gutterBottom>
        Version Details
      </Typography>
      <Paper sx={{ p: 3 }}>
        <VersionDetailContent
          version={version}
          entityType={entityType}
          entityId={entityId}
        />
      </Paper>
    </Box>
  );
};

export default VersionDetailView;
